(() => {
  // Loaded from both the mobile top bar section and the desktop header
  // snippet; guard so multiple script tags run the logic once.
  if (window.__valcoMarketSwitcherInit) return;
  window.__valcoMarketSwitcherInit = true;

  const STORAGE_KEY = 'valcoMarketTooltipDismissed';

  const storageGet = (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  };

  const storageSet = (key, value) => {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      /* private mode etc. — tooltip just shows again next visit */
    }
  };

  // Upgrade store links to the equivalent page on the other domain when a
  // cross-domain hreflang alternate exists; otherwise the configured front
  // page URL stays.
  const mapAlternateLinks = () => {
    const alternateLinks = Array.from(document.querySelectorAll('link[rel="alternate"][hreflang]'));

    document.querySelectorAll('[data-valco-market-link]').forEach((link) => {
      const targetLocale = (link.dataset.targetLocale || '').toLowerCase();
      const fallbackHost = new URL(link.href, window.location.href).hostname;
      // Match locale AND target hostname together — a same-domain alternate
      // for the locale must not shadow a later cross-domain one.
      const alternate = alternateLinks.find((candidate) => {
        const hreflang = (candidate.getAttribute('hreflang') || '').toLowerCase();
        const localeMatches = hreflang === targetLocale || (targetLocale === 'en' && hreflang.startsWith('en-'));
        if (!localeMatches || !candidate.href) return false;
        try {
          return new URL(candidate.href, window.location.href).hostname === fallbackHost;
        } catch {
          return false;
        }
      });

      if (alternate) link.href = new URL(alternate.href, window.location.href).href;
    });
  };

  // First-visit tooltip: shown once per browser when the browser language
  // suggests the visitor landed on the wrong store.
  const initTooltip = () => {
    if (window.Shopify && window.Shopify.designMode) return;
    if (storageGet(STORAGE_KEY)) return;

    const anchors = Array.from(document.querySelectorAll('[data-valco-market-anchor][data-valco-market-tooltip]'));
    if (!anchors.length) return;

    const store = anchors[0].dataset.valcoMarketStore || 'finland';
    const languages = navigator.languages?.length ? navigator.languages : [navigator.language || ''];
    const hasFinnish = languages.some((lang) => (lang || '').toLowerCase().startsWith('fi'));
    const shouldShow = store === 'finland' ? !hasFinnish : hasFinnish;
    if (!shouldShow) return;

    window.setTimeout(() => {
      const isVisible = (el) => (el.checkVisibility ? el.checkVisibility() : el.offsetWidth > 0 && el.offsetHeight > 0);
      const findVisibleAnchor = () => anchors.find(isVisible);
      let anchor = findVisibleAnchor();
      if (!anchor) return;

      const link = anchor.querySelector('[data-valco-market-link]');
      if (!link?.href) return;

      const styles = document.createElement('style');
      styles.textContent = `
        .valco-market-tooltip{position:fixed;z-index:9999;max-width:min(340px,calc(100vw - 24px));background:rgb(var(--color-background));color:rgb(var(--color-foreground));border:1px solid rgb(var(--color-border));border-radius:6px;padding:14px 40px 14px 16px;font-size:var(--type-body-sm,13px);line-height:var(--type-body-sm-lh,1.5);letter-spacing:var(--type-body-ls,0);box-shadow:0 8px 24px rgb(var(--color-shadow,17 17 17)/ .12);opacity:0;transform:translateY(-6px);transition:opacity .3s ease,transform .3s ease}
        .valco-market-tooltip.is-visible{opacity:1;transform:translateY(0)}
        .valco-market-tooltip::before{content:"";position:absolute;top:-6px;left:var(--valco-market-arrow-left,50%);width:10px;height:10px;background:rgb(var(--color-background));border-top:1px solid rgb(var(--color-border));border-left:1px solid rgb(var(--color-border));transform:translateX(-50%) rotate(45deg)}
        .valco-market-tooltip__link{color:inherit;text-decoration:none;font-weight:inherit;display:block}
        .valco-market-tooltip__link:hover,.valco-market-tooltip__link:focus-visible{text-decoration:underline}
        .valco-market-tooltip__close{position:absolute;top:6px;right:6px;width:26px;height:26px;display:flex;align-items:center;justify-content:center;background:none;border:0;color:inherit;font-size:18px;line-height:1;cursor:pointer;opacity:.6}
        .valco-market-tooltip__close:hover,.valco-market-tooltip__close:focus-visible{opacity:1}
      `;

      const bubble = document.createElement('div');
      bubble.className = 'valco-market-tooltip';

      const bubbleLink = document.createElement('a');
      bubbleLink.className = 'valco-market-tooltip__link';
      bubbleLink.href = link.href;
      bubbleLink.setAttribute('data-no-instant', '');
      bubbleLink.textContent = anchor.dataset.valcoMarketTooltip;

      const closeButton = document.createElement('button');
      closeButton.type = 'button';
      closeButton.className = 'valco-market-tooltip__close';
      closeButton.setAttribute('aria-label', anchor.dataset.valcoMarketCloseLabel || 'Close');
      closeButton.textContent = '×';

      bubble.append(bubbleLink, closeButton);

      document.head.append(styles);
      document.body.append(bubble);

      const position = () => {
        // The header renders the switcher in several places (start/end
        // contexts, mobile bar); re-anchor to whichever is visible now.
        if (!anchor.offsetWidth || !anchor.offsetHeight) {
          anchor = findVisibleAnchor();
          if (!anchor) {
            bubble.style.visibility = 'hidden';
            return;
          }
        }
        const rect = anchor.getBoundingClientRect();
        if (rect.bottom <= 0 || rect.top >= window.innerHeight) {
          bubble.style.visibility = 'hidden';
          return;
        }
        bubble.style.visibility = '';

        const margin = 12;
        const width = bubble.offsetWidth;
        const anchorCenter = rect.left + rect.width / 2;
        let left = anchorCenter - width / 2;
        left = Math.min(Math.max(left, margin), window.innerWidth - width - margin);

        bubble.style.top = `${rect.bottom + 10}px`;
        bubble.style.left = `${left}px`;
        bubble.style.setProperty('--valco-market-arrow-left', `${anchorCenter - left}px`);
      };

      // The tooltip is a fleeting "fun detail", not a dialog to manage: it
      // fades itself out after a short read, and the moment the visitor
      // engages (scrolls away, Escape, close, or the link) it's gone for good.
      const AUTO_DISMISS_MS = 7000;
      const SCROLL_DISMISS_PX = 48;
      const startScrollY = window.scrollY;
      let autoDismissTimer = 0;
      let dismissed = false;

      const clearAutoDismiss = () => {
        window.clearTimeout(autoDismissTimer);
        autoDismissTimer = 0;
      };

      const scheduleAutoDismiss = (delay) => {
        clearAutoDismiss();
        autoDismissTimer = window.setTimeout(() => dismiss(), delay);
      };

      const onScroll = () => {
        // Tiny scrolls (sticky-header jitter) just re-anchor; a real scroll
        // means the visitor moved on, so let the tooltip go.
        if (Math.abs(window.scrollY - startScrollY) > SCROLL_DISMISS_PX) {
          dismiss();
        } else {
          position();
        }
      };

      const onKeydown = (event) => {
        if (event.key === 'Escape') dismiss();
      };

      const dismiss = () => {
        if (dismissed) return;
        dismissed = true;
        storageSet(STORAGE_KEY, '1');
        clearAutoDismiss();
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', position);
        document.removeEventListener('keydown', onKeydown);
        const restoreFocus = bubble.contains(document.activeElement);
        // Animate out with the same transition used to enter, then remove.
        bubble.classList.remove('is-visible');
        const cleanup = () => {
          bubble.remove();
          styles.remove();
        };
        bubble.addEventListener('transitionend', cleanup, { once: true });
        window.setTimeout(cleanup, 400);
        if (restoreFocus) anchor.querySelector('summary, a')?.focus();
      };

      closeButton.addEventListener('click', dismiss);
      bubbleLink.addEventListener('click', () => storageSet(STORAGE_KEY, '1'));
      // Reading it? Hovering pauses the fade; a shorter grace once they leave.
      bubble.addEventListener('pointerenter', clearAutoDismiss);
      bubble.addEventListener('pointerleave', () => scheduleAutoDismiss(3000));
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', position);
      document.addEventListener('keydown', onKeydown);

      position();
      requestAnimationFrame(() => {
        position();
        bubble.classList.add('is-visible');
        scheduleAutoDismiss(AUTO_DISMISS_MS);
      });
    }, 800);
  };

  const run = () => {
    mapAlternateLinks();
    initTooltip();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
